// Generated by Claude Code
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { Eye, EyeOff } from "lucide-react";
import { State, useCustomState } from "#core/state/State.ts";
import { ApiError, apiErrorDetail } from "#core/api/Api.ts";
import PasswordResetApi from "#core/api/auth/PasswordResetApi.ts";
import appLogo from "#assets/images/app-logo.png";

const MIN_PASSWORD_LENGTH = 8;

export const Route = createFileRoute("/_authentication/reset-password")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const raw = typeof search.token === "string" ? search.token.trim() : "";
    return { token: raw || undefined };
  },
});

class ResetPasswordState extends State<ResetPasswordState> {
  password = "";
  confirmPassword = "";
  showPassword = false;
  showConfirmPassword = false;
  submitting = false;
  done = false;
  linkInvalid = false;
  error = "";

  async confirm(token: string) {
    const problem = this.#validate();
    if (problem) {
      this.reactTo.error = problem;
      return;
    }
    this.reactTo.submitting = true;
    this.reactTo.error = "";
    try {
      await new PasswordResetApi().confirmReset(token, this.password);
      this.reactTo.done = true;
    } catch (e) {
      if (e instanceof ApiError && e.response?.status === 400) {
        this.reactTo.linkInvalid = true;
      } else {
        this.reactTo.error = apiErrorDetail(e, i18n.t("resetPasswordPage.failed", { ns: "auth" }));
      }
    }
    this.reactTo.submitting = false;
  }

  #validate(): string {
    if (this.password.length < MIN_PASSWORD_LENGTH)
      return i18n.t("resetPasswordPage.tooShort", { ns: "auth", min: MIN_PASSWORD_LENGTH });
    if (this.password !== this.confirmPassword)
      return i18n.t("resetPasswordPage.mismatch", { ns: "auth" });
    return "";
  }
}

function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="container ynt-fullscreen-center">
      <div className="w-100" style={{ maxWidth: "440px" }}>
        <div className="card border-0 rounded-0 shadow-lg">
          <div className="ynt-flush-card-header">
            <div>
              <img src={appLogo} alt="YNTEQ" style={{ maxHeight: "40px" }} className="img-fluid" />
            </div>
          </div>
          <div className="card-body p-4 p-md-5">{children}</div>
        </div>
      </div>
    </div>
  );
}

function PasswordField({
  id,
  label,
  hint,
  value,
  visible,
  onChange,
  onToggleVisible,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  visible: boolean;
  onChange: (value: string) => void;
  onToggleVisible: () => void;
}) {
  const { t } = useTranslation("auth");
  return (
    <div className="mb-3">
      <label htmlFor={id} className="form-label">
        {label}
      </label>
      <div className="input-group">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="form-control"
          id={id}
          autoComplete="new-password"
          required
        />
        <button
          className="btn btn-outline-secondary d-flex align-items-center"
          type="button"
          onClick={onToggleVisible}
          aria-label={visible ? t("hidePassword") : t("showPassword")}
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {hint && <div className="form-text">{hint}</div>}
    </div>
  );
}

function RouteComponent() {
  const { token } = Route.useSearch();
  const state = useCustomState(() => new ResetPasswordState());
  const { t } = useTranslation("auth");

  if (!token || state.linkInvalid) {
    return (
      <AuthCard>
        <h5 className="mb-2">{t("resetPasswordPage.invalidTitle")}</h5>
        <p className="text-body-secondary mb-4">{t("resetPasswordPage.invalidMessage")}</p>
        <Link className="btn btn-primary ynt-full-width-center-btn" to="/forgot-password">
          {t("resetPasswordPage.requestNewLink")}
        </Link>
      </AuthCard>
    );
  }

  if (state.done) {
    return (
      <AuthCard>
        <h5 className="mb-2">{t("resetPasswordPage.successTitle")}</h5>
        <p className="text-body-secondary mb-4">{t("resetPasswordPage.successMessage")}</p>
        <Link className="btn btn-primary ynt-full-width-center-btn" to="/login">
          {t("backToLogin")}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <h5 className="mb-2">{t("resetPasswordPage.title")}</h5>
      <p className="text-body-secondary mb-4">{t("resetPasswordPage.intro")}</p>

      <form onSubmit={(e) => { e.preventDefault(); void state.confirm(token); }} noValidate>
        <PasswordField
          id="newPasswordInput"
          label={t("resetPasswordPage.newPassword")}
          hint={t("resetPasswordPage.passwordHint", { min: MIN_PASSWORD_LENGTH })}
          value={state.password}
          visible={state.showPassword}
          onChange={(value) => { state.password = value; }}
          onToggleVisible={() => { state.showPassword = !state.showPassword; }}
        />
        <PasswordField
          id="confirmPasswordInput"
          label={t("resetPasswordPage.confirmPassword")}
          value={state.confirmPassword}
          visible={state.showConfirmPassword}
          onChange={(value) => { state.confirmPassword = value; }}
          onToggleVisible={() => { state.showConfirmPassword = !state.showConfirmPassword; }}
        />

        {state.error && <div className="text-danger mb-3">{state.error}</div>}

        <button
          type="submit"
          className="btn btn-primary ynt-full-width-center-btn"
          disabled={state.submitting}
        >
          {state.submitting ? (
            <>
              {t("resetPasswordPage.submitting")}
              <div className="ms-2 spinner-border spinner-border-sm"></div>
            </>
          ) : (
            t("resetPasswordPage.submit")
          )}
        </button>
      </form>

      <div className="text-center mt-4">
        <Link className="text-orange text-decoration-none" to="/login">
          {t("backToLogin")}
        </Link>
      </div>
    </AuthCard>
  );
}
